import { useEffect, useMemo, useState } from 'react';
import { rbacAPI, getErrorMessage } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Search, RefreshCw, ShieldAlert, ShieldX } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  green: {
    label: 'RBAC explícito',
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    icon: ShieldCheck,
  },
  yellow: {
    label: 'Fallback legado',
    className: 'border-amber-200 bg-amber-50 text-amber-700',
    icon: ShieldAlert,
  },
  red: {
    label: 'Público',
    className: 'border-red-200 bg-red-50 text-red-700',
    icon: ShieldX,
  },
};

export const RBACCoverageTab = () => {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');

  const loadCoverage = async () => {
    try {
      setLoading(true);
      const response = await rbacAPI.getCoverage();
      setCoverage(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao carregar cobertura RBAC'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoverage();
  }, []);

  const moduleOptions = useMemo(() => {
    return (coverage?.modules || []).map((item) => item.module).sort((a, b) => a.localeCompare(b));
  }, [coverage]);

  const filteredRoutes = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (coverage?.routes || []).filter((route) => {
      const matchesStatus = statusFilter === 'all' || route.color === statusFilter;
      const matchesModule = moduleFilter === 'all' || route.module === moduleFilter;
      const haystack = `${route.method} ${route.path} ${route.module} ${route.reason} ${(route.fallback_permissions || []).join(' ')}`.toLowerCase();
      const matchesSearch = !normalizedSearch || haystack.includes(normalizedSearch);
      return matchesStatus && matchesModule && matchesSearch;
    });
  }, [coverage, moduleFilter, search, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="rbac-coverage-loading">
        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        A carregar cobertura RBAC...
      </div>
    );
  }

  const summary = coverage?.summary || { total: 0, green: 0, yellow: 0, red: 0, strict_percent: 0, guarded_percent: 0 };

  return (
    <div className="space-y-5" data-testid="rbac-coverage-tab">
      <div className="grid gap-4 md:grid-cols-4">
        {[
          { key: 'total', label: 'Endpoints auditados', value: summary.total, className: 'border-slate-200 bg-slate-50 text-slate-700' },
          { key: 'green', label: 'RBAC explícito', value: summary.green, className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
          { key: 'yellow', label: 'Fallback legado', value: summary.yellow, className: 'border-amber-200 bg-amber-50 text-amber-700' },
          { key: 'red', label: 'Públicos', value: summary.red, className: 'border-red-200 bg-red-50 text-red-700' },
        ].map((card) => (
          <Card key={card.key} className={card.className} data-testid={`rbac-coverage-summary-${card.key}`}>
            <CardContent className="pt-5">
              <p className="text-xs uppercase tracking-wide opacity-80">{card.label}</p>
              <p className="mt-2 text-3xl font-semibold">{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card data-testid="rbac-coverage-health-card">
        <CardHeader>
          <CardTitle className="text-base">Estado Global</CardTitle>
          <CardDescription>
            Protecção estrita: <strong>{summary.strict_percent}%</strong> • Guardado total (explícito + fallback): <strong>{summary.guarded_percent}%</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
            <div className="flex flex-wrap gap-2" data-testid="rbac-coverage-legend">
              {(coverage?.legend || []).map((item) => (
                <Badge key={item.color} variant="outline" className={STATUS_META[item.color]?.className || ''}>
                  {item.label}
                </Badge>
              ))}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={loadCoverage} data-testid="rbac-coverage-refresh-button">
                <RefreshCw className="mr-2 h-4 w-4" />
                Actualizar
              </Button>
            </div>
          </div>

          <div className="grid gap-3 xl:grid-cols-3" data-testid="rbac-coverage-modules-grid">
            {(coverage?.modules || []).map((module) => (
              <div
                key={module.module}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                data-testid={`rbac-module-card-${module.module}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium capitalize">{module.module.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-muted-foreground">{module.total} endpoints</p>
                  </div>
                  <Badge variant="outline" className={STATUS_META[module.status]?.className || ''}>
                    {module.strict_percent}%
                  </Badge>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <span>Verde: {module.green}</span>
                  <span>Amarelo: {module.yellow}</span>
                  <span>Vermelho: {module.red}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="rbac-coverage-legend-card">
        <CardHeader>
          <CardTitle className="text-base">Legenda Operacional</CardTitle>
          <CardDescription>
            Referência rápida para leitura da matriz RBAC e decisão de priorização.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="rbac-legend-green">
              <div className="flex items-center gap-2 text-emerald-800">
                <ShieldCheck className="h-4 w-4" />
                <p className="font-medium">Verde — RBAC explícito</p>
              </div>
              <p className="mt-2 text-xs text-emerald-700">Endpoint com permissão definida diretamente na rota.</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="rbac-legend-yellow">
              <div className="flex items-center gap-2 text-amber-800">
                <ShieldAlert className="h-4 w-4" />
                <p className="font-medium">Amarelo — Fallback legado</p>
              </div>
              <p className="mt-2 text-xs text-amber-700">Rota protegida por regra antiga. Recomenda-se migração para RBAC explícito.</p>
            </div>
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="rbac-legend-red">
              <div className="flex items-center gap-2 text-red-800">
                <ShieldX className="h-4 w-4" />
                <p className="font-medium">Vermelho — Público</p>
              </div>
              <p className="mt-2 text-xs text-red-700">Sem proteção RBAC. Confirmar se deve permanecer público.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="rbac-coverage-routes-card">
        <CardHeader>
          <CardTitle className="text-base">Rotas e Endpoints</CardTitle>
          <CardDescription>
            Painel visual para identificar imediatamente endpoints já protegidos e os que ainda dependem do fallback legado.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 lg:grid-cols-[1.2fr_220px_220px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Pesquisar endpoint, módulo ou permissão..."
                className="pl-9"
                data-testid="rbac-coverage-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="rbac-coverage-status-filter">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os estados</SelectItem>
                <SelectItem value="green">RBAC explícito</SelectItem>
                <SelectItem value="yellow">Fallback legado</SelectItem>
                <SelectItem value="red">Público</SelectItem>
              </SelectContent>
            </Select>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger data-testid="rbac-coverage-module-filter">
                <SelectValue placeholder="Módulo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os módulos</SelectItem>
                {moduleOptions.map((module) => (
                  <SelectItem key={module} value={module}>{module}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-slate-200" data-testid="rbac-coverage-table-wrapper">
            <table className="min-w-full text-sm" data-testid="rbac-coverage-table">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Método</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Endpoint</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Módulo</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Estado</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Fallback / Nota</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoutes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground" data-testid="rbac-coverage-empty-state">
                      Nenhuma rota encontrada com os filtros actuais.
                    </td>
                  </tr>
                ) : (
                  filteredRoutes.map((route) => {
                    const meta = STATUS_META[route.color] || STATUS_META.red;
                    const Icon = meta.icon;
                    return (
                      <tr key={`${route.method}-${route.path}`} className="border-t align-top" data-testid={`rbac-route-${route.method}-${route.path.replace(/[^a-zA-Z0-9]+/g, '-')}`}>
                        <td className="px-4 py-3 font-mono text-xs font-semibold">{route.method}</td>
                        <td className="px-4 py-3">
                          <div className="font-mono text-xs text-slate-800">{route.path}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{route.source_file || 'origem desconhecida'} • {route.name}</div>
                        </td>
                        <td className="px-4 py-3 capitalize">{route.module.replace(/_/g, ' ')}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={meta.className}>
                            <Icon className="mr-1 h-3.5 w-3.5" />
                            {meta.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">
                          <div>{route.reason}</div>
                          {(route.fallback_permissions || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {route.fallback_permissions.map((permission) => (
                                <Badge key={permission} variant="secondary" className="bg-slate-100 text-slate-700">
                                  {permission}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};